import { Action, State, StateContext, Store } from '@ngxs/store';
import { append, patch } from '@ngxs/store/operators';
import { map, take, tap } from 'rxjs/operators';

import { BillsRepository } from '@data-layer/repositories';
import { Injectable } from '@angular/core';
import { PaginatedResultModel } from '@data-layer/models/common';
import { BillItemModel, BillModel } from '@data-layer/models/payments';
import { UserBillingStateModel } from './billing-state.model';
import { FetchBillList, OffsetChange, ResetBillListData, UpdateLoadingState } from './billing.actions';
import { SearchHelper } from '@helpers/search-helper';
import { BILL_SERVICE_PRODUCT_CODES } from '@constants/bill-service-product-codes';
import { enumProductServiceType } from '@app/data-layer/enums/common/enumProductServiceType';
import { enumPaymentEntity } from '@app/modules/payment/enums';

const defaults: UserBillingStateModel = {
  isLoading: true,
  offset: 0,
  limit: 20,
  total: 0,
  billList: [],
};

@State<UserBillingStateModel>({
  name: 'userBilling',
  defaults,
})
@Injectable()
export class UserBillingState {
  constructor(private billsRepository: BillsRepository) {}

  @Action(UpdateLoadingState)
  public updateLoadingState(ctx: StateContext<UserBillingStateModel>, { payload }: UpdateLoadingState) {
    return ctx.patchState({ isLoading: payload });
  }

  @Action(OffsetChange)
  public offsetChange(ctx: StateContext<UserBillingStateModel>, { payload }: OffsetChange) {
    return ctx.patchState({ offset: payload });
  }

  @Action(ResetBillListData)
  public resetBillListData(ctx: StateContext<UserBillingStateModel>): void {
    ctx.patchState({ ...defaults });
  }

  @Action(FetchBillList, { cancelUncompleted: true })
  public fetchBillList(ctx: StateContext<UserBillingStateModel>, { payload }: FetchBillList) {
    return this.billsRepository.searchAll(SearchHelper.convertFiltersToParams(payload.filters), true).pipe(
      map((result: PaginatedResultModel<BillModel>) => {
        const filters = payload.filters;
        let paginatedResultBills: PaginatedResultModel<BillModel> = result;
        if (!!payload.companyId) {
          let billsItemsList: BillModel[] =
            !!result?.items && result?.items.length > 0
              ? result?.items.map((bill) => {
                  const searchParameter = filters?.searchParameter;
                  const isBillMatchingSearchParameter = !!searchParameter
                    ? String(bill?.invoiceDocumentNumber || '').includes(searchParameter)
                    : false;

                  const companyFilteredBillItems = bill.items.filter((billItem) => billItem?.companyId === payload.companyId);

                  const serviceFilteredBillItems: BillItemModel[] = !!filters?.productCodes
                    ? this.getFilteredBillItemsByPaymentEntity(companyFilteredBillItems, filters?.productCodes)
                    : [];

                  const filteredBillItems: BillItemModel[] = !!filters?.productCodes
                    ? isBillMatchingSearchParameter
                      ? serviceFilteredBillItems
                      : this.getFilteredBillItemsBySearchParameter(serviceFilteredBillItems, searchParameter)
                    : isBillMatchingSearchParameter
                    ? companyFilteredBillItems
                    : this.getFilteredBillItemsBySearchParameter(companyFilteredBillItems, searchParameter);

                  return {
                    ...bill,
                    items: filteredBillItems,
                  };
                })
              : [];

          paginatedResultBills = {
            ...result,
            items: billsItemsList.filter((bill) => bill.items.length),
          };
        }

        return !!payload.companyId
          ? paginatedResultBills
          : this.getFilteredBillsByPaymentEntity(paginatedResultBills, filters?.productCodes || [], filters?.searchParameter);
      }),
      tap((response: PaginatedResultModel<BillModel>) =>
        ctx.setState(
          patch({
            billList: append([...response.items]),
            total: response.total,
          }),
        ),
      ),
      take(1),
    );
  }

  private getFilteredBillsByPaymentEntity(
    billList: PaginatedResultModel<BillModel>,
    productCodes: string[],
    searchParameter?: string,
  ): PaginatedResultModel<BillModel> {
    const billListItems = billList.items.map((item) => {
      const isBillMatchingSearchParameter = !!searchParameter ? String(item?.invoiceDocumentNumber || '').includes(searchParameter) : false;

      const serviceFilteredBillItems: BillItemModel[] = productCodes.length
        ? this.getFilteredBillItemsByPaymentEntity(item.items, productCodes)
        : [];

      const filteredBillItems: BillItemModel[] = productCodes.length
        ? isBillMatchingSearchParameter
          ? serviceFilteredBillItems
          : this.getFilteredBillItemsBySearchParameter(serviceFilteredBillItems, searchParameter)
        : isBillMatchingSearchParameter
        ? item.items
        : this.getFilteredBillItemsBySearchParameter(item.items, searchParameter);

      return {
        ...item,
        items: filteredBillItems,
      };
    });

    return {
      ...billList,
      items: billListItems.filter((bill) => bill.items.length),
    };
  }

  private getFilteredBillItemsBySearchParameter(billItems: BillItemModel[], searchParameter: string): BillItemModel[] {
    return billItems.filter((billItem) =>
      !!searchParameter ? (!!billItem?.referenceNumber ? billItem?.referenceNumber.includes(searchParameter) : false) : true,
    );
  }

  private getFilteredBillItemsByPaymentEntity(billItems: BillItemModel[], productCodes: string[]): BillItemModel[] {
    const serviceCodes = this.getGetServiceByProductCodes(productCodes);
    const includesIsfService = serviceCodes.includes(enumProductServiceType.ISF);
    const includesEsService = serviceCodes.includes(enumProductServiceType.ENTRY_SUMMARY);
    const includesLclService = serviceCodes.includes(enumProductServiceType.INTERNATIONAL_SHIPPING);
    const includesBondsService = serviceCodes.includes(enumProductServiceType.CUSTOMS_BONDS);
    const includesMarineInsuranceService = serviceCodes.includes(enumProductServiceType.MARINE_INSURANCE);
    const includesPlanService = serviceCodes.includes(enumProductServiceType.PLAN);
    const includesAssignedCbpNumberRequestService = serviceCodes.includes(enumProductServiceType.ASSIGNED_CBP_NUMBER_REQUEST);

    let isfFilteredBillItems: BillItemModel[] = includesIsfService
      ? billItems.filter((item) => item?.referenceNumber?.startsWith(enumPaymentEntity.ISF))
      : [];

    let esFilteredBillItems: BillItemModel[] = includesEsService
      ? billItems.filter((item) => item?.referenceNumber?.startsWith(enumPaymentEntity.ES))
      : [];
    let lclFilteredBillItems: BillItemModel[] = includesLclService
      ? billItems.filter((item) => item?.referenceNumber?.startsWith(enumPaymentEntity.SH))
      : [];

    let bondsFilteredBillItems: BillItemModel[] = includesBondsService
      ? billItems.filter((item) => item?.referenceNumber?.startsWith(enumPaymentEntity.BR))
      : [];

    let marineInsuranceFilteredBillItems: BillItemModel[] = includesMarineInsuranceService
      ? billItems.filter((item) => item?.referenceNumber?.startsWith(enumPaymentEntity.MI))
      : [];

    let planFilteredBillItems: BillItemModel[] = includesPlanService
      ? billItems.filter((item) => (BILL_SERVICE_PRODUCT_CODES[enumProductServiceType.PLAN] as string[]).includes(item.productCode))
      : [];

    let assignedCbpNumberRequestFilteredBillItems: BillItemModel[] = includesAssignedCbpNumberRequestService
      ? billItems.filter((item) =>
          (BILL_SERVICE_PRODUCT_CODES[enumProductServiceType.ASSIGNED_CBP_NUMBER_REQUEST] as string[]).includes(item.productCode),
        )
      : [];

    return !!productCodes && productCodes.length > 0
      ? [
          ...isfFilteredBillItems,
          ...esFilteredBillItems,
          ...lclFilteredBillItems,
          ...bondsFilteredBillItems,
          ...marineInsuranceFilteredBillItems,
          ...planFilteredBillItems,
          ...assignedCbpNumberRequestFilteredBillItems,
        ]
      : billItems;
  }

  private getGetServiceByProductCodes(productCodes: string[]): enumProductServiceType[] {
    const identifiedProductCodesEntries = !!productCodes
      ? Object.entries(BILL_SERVICE_PRODUCT_CODES)
          .filter(([_, value]: [string, any]) => productCodes.some((pc) => value.includes(pc)))
          .map(([key, _]: [string, any]) => Number(key))
      : [];

    return identifiedProductCodesEntries;
  }
}
